import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Space,
  Table, Form, Select
} from 'antd';
import { FileExcelFilled, SearchOutlined } from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';
import Highlighter from 'react-highlight-words';
import moment from 'moment';
import { Excel } from 'antd-table-saveas-excel';
import { Link } from 'react-router-dom';
import { RmCountPriceListService } from '@gtpl/shared-services/raw-material-procurement';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { SkuPriceCurrentDto } from '@gtpl/shared-models/raw-material-procurement';
import { SkuService } from '@gtpl/shared-services/masters';

export function SkuPriceView() {
  const service = new RmCountPriceListService();
  const [data, setData] = useState<SkuPriceCurrentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [skuOptions, setSkuOptions] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const searchInput = useRef<any>(null);
  const [form] = Form.useForm();
  const skuService = new SkuService();

  useEffect(() => {
    fetchData();
    loadSkus();
  }, []);

  const fetchData = async (skuId?: number) => {
    setLoading(true);
    try {
      const res = await service.getCurrentSkuPrices({ skuId });
      if (res.status) {
        setData(res.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data?.length || 0,
        }));
      } else {
        setData([]);
        AlertMessages.getErrorMessage(res.internalMessage || 'No records found');
      }
    } catch (err: any) {
      AlertMessages.getErrorMessage(err?.message || 'Failed to load SKU prices');
    } finally {
      setLoading(false);
    }
  };

  const loadSkus = async () => {
    try {
      const res = await skuService.getActiveSkuCodes();
      if (res.status) {
        setSkuOptions(res.data || []);
      }
    } catch {
      AlertMessages.getErrorMessage('Failed to load SKU list');
    }
  };
  const onSearch = (values: any) => {
    fetchData(values.skuId);
  };
  const getColumnSearchProps = (dataIndex: string): ColumnProps<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setSearchText(selectedKeys[0] as string);
            setSearchedColumn(dataIndex);
          }}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              setSearchText(selectedKeys[0] as string);
              setSearchedColumn(dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              setSearchText('');
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(String(value).toLowerCase())
        : false,
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
          text
        ),
  });

  const columns: ColumnProps<any>[] = [
    {
      title: 'S.No',
      key: 'sNo',
      width: 70,
      render: (_text, _record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    
    {
      title: 'SKU Name',
      dataIndex: 'skuName',
      key: 'skuName',
      sorter: (a, b) => String(a.skuName).localeCompare(String(b.skuName)),
      ...getColumnSearchProps('skuName'),
    },
    {
      title: 'Current Price',
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => Number(a.price) - Number(b.price),
      render: (val: number) => Number(val).toFixed(2),
    },
    {
      title: 'Effective From',
      dataIndex: 'effectiveFrom',
      key: 'effectiveFrom',
      sorter: (a, b) => moment(a.effectiveFrom).unix() - moment(b.effectiveFrom).unix(),
      render: (val: string) => moment(val).format('DD-MM-YYYY'),
    },
  ];

  const exportExcel = () => {
    const excel = new Excel();
    excel
      .addSheet('Product Sku Rates')
      .addColumns([
        { title: 'SKU Name', dataIndex: 'skuName' },
        { title: 'Current Price', dataIndex: 'price' },
        {
          title: 'Effective From',
          dataIndex: 'effectiveFrom',
          render: (val: string) => moment(val).format('DD-MM-YYYY'),
        },
      ])
      .addDataSource(
        data.map((row) => ({
          ...row,
          effectiveFrom: moment(row.effectiveFrom).format('DD-MM-YYYY'),
        })),
      )
      .saveAs('current-sku-prices.xlsx');
  };

  return (
    <Card
      title={<span style={{ color: 'white' }}>Product Sku Rates</span>}
style={{ margin: 20 }}
      headStyle={{
        backgroundColor: '#69c0ff',
        border: 0,
        textAlign: 'center',
      }}      extra={
        <Space>
          <Link to="/sku-price-form">
            <Button type="primary">Add Price</Button>
          </Link>
          <Link to="/sku-price-history">
            <Button>Historical View</Button>
          </Link>
          {/* <Button icon={<FileExcelFilled />} onClick={exportExcel}>
            Export Excel
          </Button> */}
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSearch}>
        <Row gutter={12}>
          <Col xs={12} md={6}>
            <Form.Item name="skuId" label="Product Sku">
              <Select showSearch allowClear placeholder="Select Product Sku" optionFilterProp="children">
                {skuOptions.map((sku) => (
                  <Select.Option key={sku.skuCodeId} value={sku.skuCodeId}>
                    {(sku.shortCode)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {/* <Col xs={24} md={10}>
            <Form.Item name="dateRange" label="Date Range">
              <RangePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
            </Form.Item>
          </Col> */}
          <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields();
                    fetchData();
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        columns={columns}
        dataSource={data}
        rowKey={(record) => `${record.skuId}`}
        loading={loading}
        pagination={{
          total: data.length,
        }}
        scroll={{ x: true }}
      />
    </Card>
  );
}

export default SkuPriceView;
